import React, { useEffect, useState } from 'react';
import Button from '../UI/Button';
import Card from '../UI/Card';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Instructions } from '../../api/services/platformSettings.service';

interface InstructionsTabProps {
  value: Instructions;
  onSave: (instructions: Instructions) => Promise<void>;
  saving: boolean;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const InstructionsTab: React.FC<InstructionsTabProps> = ({ value, onSave, saving }) => {
  const [form, setForm] = useState<Instructions>(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(value);
    setDirty(false);
  }, [value]);

  const updateStep = (i: number, v: string) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.map((s, idx) => (idx === i ? v : s)),
    }));
    setDirty(true);
  };

  const addStep = () => {
    setForm((prev) => ({ ...prev, steps: [...prev.steps, ''] }));
    setDirty(true);
  };

  const removeStep = (i: number) => {
    setForm((prev) => ({ ...prev, steps: prev.steps.filter((_, idx) => idx !== i) }));
    setDirty(true);
  };

  const moveStep = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= form.steps.length) return;
    setForm((prev) => {
      const next = [...prev.steps];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...prev, steps: next };
    });
    setDirty(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Instructions</h2>

        <div className="space-y-4">
          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => {
                setForm((p) => ({ ...p, title: e.target.value }));
                setDirty(true);
              }}
              className={inputClass}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelClass}>Steps</label>
              <button
                onClick={addStep}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Step
              </button>
            </div>

            {form.steps.length === 0 && (
              <p className="text-xs text-gray-500">No steps yet.</p>
            )}

            <div className="space-y-2">
              {form.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveStep(i, -1)}
                      disabled={i === 0}
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-[10px]"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveStep(i, 1)}
                      disabled={i === form.steps.length - 1}
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-[10px]"
                    >
                      ▼
                    </button>
                  </div>
                  <span className="text-xs font-medium text-gray-400 w-5 text-center">
                    {i + 1}
                  </span>
                  <input
                    type="text"
                    value={step}
                    onChange={(e) => updateStep(i, e.target.value)}
                    placeholder={`Step ${i + 1}`}
                    className={`${inputClass} flex-1`}
                  />
                  <button
                    onClick={() => removeStep(i)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-500 flex-shrink-0"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Footer</label>
            <textarea
              value={form.footer}
              onChange={(e) => {
                setForm((p) => ({ ...p, footer: e.target.value }));
                setDirty(true);
              }}
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" disabled={!dirty || saving} onClick={() => onSave(form)}>
          {saving ? 'Saving...' : 'Save Instructions'}
        </Button>
      </div>
    </div>
  );
};

export default InstructionsTab;